import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Modal, Popconfirm, Tag, Image, Select, Card, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, LinkOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { useProduse } from '../hooks/useProduse';
import { useCategories } from '../hooks/useCategories';
import { useFurnizori } from '../hooks/useFurnizori';
import ProdusForm from './ProdusForm';
import LinkFurnizorModal from './LinkFurnizorModal';
import { useStateContext } from '../../../../../contexts/ContextProvider';

const { Search } = Input;
const { Option } = Select;

function ProduseList() {
  const { user } = useStateContext();

  const { produse, loading, pagination, fetchProduse, createProdus, updateProdus, deleteProdus, linkFurnizorToProdus } = useProduse();
  const { furnizori, fetchFurnizori } = useFurnizori();
  const { categories } = useCategories();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLinkModalVisible, setIsLinkModalVisible] = useState(false);
  const [editingProdus, setEditingProdus] = useState(null);
  const [selectedProdus, setSelectedProdus] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    category: null,
    furnizor: null
  });
  const [showFilters, setShowFilters] = useState(false);

  // Funcție helper pentru a gestiona URL-urile imaginilor
  const getImageUrl = (url) => {
    if (!url) return '/placeholder-product.png';
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_API_URL}${url}`;
  };

  useEffect(() => {
    fetchProduse(1, pagination.pageSize || 10, {
      sort_by: 'data_adaugare',
      sort_order: 'desc'
    });
  }, []);

  useEffect(() => {
    fetchFurnizori();
  }, [fetchFurnizori]);

  const getCategoryFullName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    if (!category) return '-';
    
    if (category.parent_id) {
      const parent = categories.find(cat => cat.id === category.parent_id);
      return parent ? `${parent.category_name} > ${category.category_name}` : category.category_name;
    }
    
    return category.category_name;
  };

  const getOrganizedCategories = () => {
    const parentCategories = categories.filter(cat => !cat.parent_id);
    const childCategories = categories.filter(cat => cat.parent_id);
    
    const organized = [];
    
    parentCategories.forEach(parent => {
      organized.push({
        id: parent.id,
        name: parent.category_name,
        fullName: parent.category_name,
        isParent: true
      });
      
      const children = childCategories.filter(child => child.parent_id === parent.id);
      children.forEach(child => {
        organized.push({
          id: child.id,
          name: child.category_name,
          fullName: `${parent.category_name} > ${child.category_name}`,
          isParent: false,
          parentName: parent.category_name
        });
      });
    });
    
    const orphanCategories = categories.filter(cat => 
      !cat.parent_id && !parentCategories.find(p => p.id === cat.id)
    );
    orphanCategories.forEach(orphan => {
      organized.push({
        id: orphan.id,
        name: orphan.category_name,
        fullName: orphan.category_name,
        isParent: true
      });
    });
    
    return organized;
  };

  const getAllFurnizori = () => {
    return furnizori.map(furnizor => ({
      id: furnizor._id,
      nume: furnizor.denumire,
      brand_name: furnizor.brand_name
    })).sort((a, b) => a.nume.localeCompare(b.nume));
  };

  const columns = [
    {
      title: 'Imagine',
      dataIndex: 'poza_principala_url',
      key: 'poza_principala_url',
      width: 80,
      render: (url) => (
        <Image
          width={50}
          height={50}
          src={getImageUrl(url)}
          fallback="/placeholder-product.png"
          className="rounded-lg object-cover"
        />
      ),
    },
    {
      title: 'Denumire',
      dataIndex: 'denumire',
      key: 'denumire',
      sorter: true,
      render: (text, record) => (
        <div>
          <div className="font-medium text-gray-900">{text}</div>
          {record.sku_intern && (
            <div className="text-sm text-gray-500">SKU: {record.sku_intern}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Categorie',
      dataIndex: 'category_id',
      key: 'category_id',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="p-3" style={{ width: 300 }}>
          <div className="mb-2 text-sm font-medium text-gray-700">Selectează categoria:</div>
          <Select
            placeholder="Toate categoriile"
            value={selectedKeys[0]}
            onChange={(value) => {
              setSelectedKeys(value ? [value] : []);
              confirm();
            }}
            style={{ width: '100%', marginBottom: 8 }}
            allowClear
            showSearch
            filterOption={(input, option) => {
              const category = getOrganizedCategories().find(cat => cat.id === option.value);
              return category ? category.fullName.toLowerCase().includes(input.toLowerCase()) : false;
            }}
          >
            {getOrganizedCategories().map(category => (
              <Option key={category.id} value={category.id}>
                <div className={`${category.isParent ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                  {category.isParent ? (
                    <span className="flex items-center">
                      📁 {category.name}
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <span className="mr-2 text-gray-400">└─</span>
                      {category.name}
                    </span>
                  )}
                </div>
              </Option>
            ))}
          </Select>
          <Space>
            <Button
              type="primary"
              size="small"
              onClick={() => confirm()}
            >
              Filtrează
            </Button>
            <Button
              size="small"
              onClick={() => {
                clearFilters();
                confirm();
              }}
            >
              Resetează
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => record.category_id === value,
      render: (categoryId) => {
        const fullName = getCategoryFullName(categoryId);
        const category = categories.find(cat => cat.id === categoryId);
        const isChild = category?.parent_id;
        
        return (
          <div className="flex flex-col">
            <span className={`text-sm px-2 py-1 rounded ${
              isChild 
                ? 'bg-green-100 text-green-800' 
                : 'bg-blue-100 text-blue-800'
            }`}>
              {fullName}
            </span>
          </div>
        );
      },
    },
    {
      title: 'Unitate măsură',
      dataIndex: 'unit_of_measure',
      key: 'unit_of_measure',
      render: (unit) => unit || '-',
    },
    {
      title: 'Furnizori',
      dataIndex: 'furnizori',
      key: 'furnizori',
      width: 300,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="p-2">
          <Select
            placeholder="Selectează furnizorul"
            value={selectedKeys[0]}
            onChange={(value) => {
              setSelectedKeys(value ? [value] : []);
              confirm();
            }}
            style={{ width: 200, marginBottom: 8, display: 'block' }}
            allowClear
          >
            {getAllFurnizori().map(furnizor => (
              <Option key={furnizor.id} value={furnizor.id}>
                {furnizor.brand_name ? `${furnizor.nume} (${furnizor.brand_name})` : furnizor.nume}
              </Option>
            ))}
          </Select>
          <Space>
            <Button
              type="primary"
              size="small"
              onClick={() => {
                confirm();
              }}
            >
              Filtrează
            </Button>
            <Button
              size="small"
              onClick={() => {
                clearFilters();
                confirm();
              }}
            >
              Resetează
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => {
        return record.furnizori && record.furnizori.some(furnizor => furnizor.furnizor_id === value);
      },
      render: (furnizori, record) => {
        if (!furnizori || furnizori.length === 0) {
          return (
            <div className="flex items-center">
              <Tag color="default">Fără furnizori</Tag>
              <Button
                type="link"
                size="small"
                icon={<LinkOutlined />}
                onClick={() => {
                  setSelectedProdus(record);
                  setIsLinkModalVisible(true);
                }}
                className="ml-2"
              >
                Adaugă
              </Button>
            </div>
          );
        }

        const sorted = [...furnizori].sort((a, b) => a.pret - b.pret);
        return (
          <div className="space-y-2">
            {sorted.map((furnizor, index) => {
              const displayName = furnizor.brand_name
                ? `${furnizor.nume} (${furnizor.brand_name})`
                : furnizor.nume || 'Necunoscut';
              return (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Tag color={index === 0 ? 'green' : 'blue'} className="text-xs">
                      {displayName}
                    </Tag>
                    <span className="ml-2 text-sm text-gray-600">
                      {user.role_id !== 2 ? `${furnizor.pret} MDL` : ``}
                    </span>
                  </div>
                  {index === 0 && (
                    <Tooltip title="Cel mai bun preț">
                      <span className="text-green-500 text-xs">✓</span>
                    </Tooltip>
                  )}
                </div>
              );
            })}
            {user.role_id !== 2 && <Button
              type="link"
              size="small"
              icon={<LinkOutlined />}
              onClick={() => {
                setSelectedProdus(record);
                setIsLinkModalVisible(true);
              }}
            >
              Gestionează furnizori
            </Button>}
          </div>
        );
      },
    },
    {
      title: 'Preț de vânzare',
      dataIndex: 'pret_vanzare',
      key: 'pret_vanzare',
      render: (pret) => pret ? `${pret} MDL` : '-',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Activ' : 'Inactiv'}
        </Tag>
      ),
    },
    {
      title: 'Acțiuni',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => {
              setEditingProdus(record);
              setIsModalVisible(true);
            }}
          />
          <Popconfirm
            title="Ești sigur că vrei să ștergi acest produs?"
            onConfirm={() => deleteProdus(record.id)}
            okText="Da"
            cancelText="Nu"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleTableChange = (paginationInfo, filters, sorter) => {
    const searchParams = {
      search: searchText,
      category_id: filters.category_id?.[0] || filters.category,
      furnizor: filters.furnizori?.[0] || filters.furnizor,
      sort_by: sorter.field,
      sort_order: sorter.order === 'ascend' ? 'asc' : 'desc'
    };
    
    fetchProduse(paginationInfo.current, paginationInfo.pageSize, searchParams);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    fetchProduse(1, pagination.pageSize, { 
      search: value,
      category_id: filters.category,
      furnizor: filters.furnizor
    });
  };

  const handleModalSubmit = async (values) => {
    let success;
    if (editingProdus) {
      success = await updateProdus(editingProdus.id, values);
    } else {
      success = await createProdus(values);
    }
    
    if (success) {
      setIsModalVisible(false);
      setEditingProdus(null);
    }
  };

  const handleLinkFurnizor = async (furnizorData) => {
    console.log(selectedProdus.id, furnizorData, "Link");
    const success = await linkFurnizorToProdus(selectedProdus.id, furnizorData);
    if (success) {
      setIsLinkModalVisible(false);
      setSelectedProdus(null);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Produse</h2>
            <p className="text-gray-600">Gestionează produsele companiei</p>
          </div>
          <Space>
            <Button
              icon={<FilterOutlined />}
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? 'bg-blue-100' : ''}
            >
              Filtre
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchProduse(1, pagination.pageSize)}
            >
              Reîmprospătează
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingProdus(null);
                setIsModalVisible(true);
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Adaugă Produs
            </Button>
          </Space>
        </div>

        <Card className="mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <Search
              placeholder="Caută după denumire, SKU, furnizor sau categorie..."
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              onSearch={handleSearch}
              className="flex-1 max-w-md"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            
            {showFilters && (
              <>
                <Select
                  placeholder="Toate categoriile"
                  value={filters.category}
                  onChange={(value) => {
                    setFilters(prev => ({ ...prev, category: value }));
                    fetchProduse(1, pagination.pageSize, {
                      search: searchText,
                      category_id: value,
                      furnizor: filters.furnizor
                    });
                  }}
                  style={{ width: 250 }}
                  allowClear
                  showSearch
                  filterOption={(input, option) => {
                    const category = getOrganizedCategories().find(cat => cat.id === option.value);
                    return category ? category.fullName.toLowerCase().includes(input.toLowerCase()) : false;
                  }}
                >
                  {getOrganizedCategories().map(category => (
                    <Option key={category.id} value={category.id}>
                      <div className={`${category.isParent ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                        {category.isParent ? (
                          <span className="flex items-center">
                            📁 {category.name}
                          </span>
                        ) : (
                          <span className="flex items-center">
                            <span className="mr-2 text-gray-400">└─</span>
                            {category.name}
                          </span>
                        )}
                      </div>
                    </Option>
                  ))}
                </Select>

                <Select
                  placeholder="Toți furnizorii"
                  value={filters.furnizor}
                  onChange={(value) => {
                    setFilters(prev => ({ ...prev, furnizor: value }));
                    fetchProduse(1, pagination.pageSize, {
                      search: searchText,
                      category_id: filters.category,
                      furnizor: value
                    });
                  }}
                  style={{ width: 200 }}
                  allowClear
                >
                  {getAllFurnizori().map(furnizor => (
                    <Option key={furnizor.id} value={furnizor.id}>
                      {furnizor.nume}
                    </Option>
                  ))}
                </Select>
              </>
            )}
          </div>
        </Card>

        <Table
          columns={columns}
          dataSource={produse}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} din ${total} produse`,
          }}
          onChange={handleTableChange}
          className="bg-white rounded-lg shadow-sm"
          scroll={{ x: 1200 }}
        />
      </div>

      <Modal
        title={editingProdus ? 'Editează Produs' : 'Adaugă Produs Nou'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingProdus(null);
        }}
        footer={null}
        width={800}
        destroyOnClose
      >
        <ProdusForm
          initialValues={editingProdus}
          onSubmit={handleModalSubmit}
          onCancel={() => {
            setIsModalVisible(false);
            setEditingProdus(null);
          }}
        />
      </Modal>

      <LinkFurnizorModal
        visible={isLinkModalVisible}
        produs={selectedProdus}
        onSubmit={handleLinkFurnizor}
        onCancel={() => {
          setIsLinkModalVisible(false);
          setSelectedProdus(null);
        }}
        onRefresh={() => fetchProduse(1, pagination.pageSize)}
      />
    </div>
  );
}

export default ProduseList; 